import { apiClient } from "./client";
import type { Paginated, PageQuery } from "@/types/common";
import type { IntegrationSettings, RolePermissionRow } from "@/types/settings";
import type { User } from "@/types/user";
import type { Role } from "@/types/enums";
import type { PermissionKey } from "@/types/permissions";

export async function fetchIntegrationSettings(): Promise<IntegrationSettings> {
  const { data } = await apiClient.get<IntegrationSettings>("/settings/integrations");
  return data;
}

export async function updateIntegrationSettings(payload: Partial<IntegrationSettings>): Promise<IntegrationSettings> {
  const { data } = await apiClient.patch<IntegrationSettings>("/settings/integrations", payload);
  return data;
}

export interface PublicSettings {
  gradeScale: number;
  gradesOn: boolean;
  timeSlots: string[];
}

/** Доступно любому аутентифицированному пользователю (напр. преподавателю в «Журнале урока»). */
export async function fetchPublicSettings(): Promise<PublicSettings> {
  const { data } = await apiClient.get<PublicSettings>("/settings/public");
  return data;
}

export interface UsersQuery extends PageQuery {
  role?: Role;
  search?: string;
}

export async function fetchUsers(query: UsersQuery = {}): Promise<Paginated<User>> {
  const { data } = await apiClient.get<Paginated<User>>("/users", { params: query });
  return data;
}

export interface UserCreateInput {
  role: Role;
  fullName: string;
  login: string;
  password: string;
  phone?: string;
  branchId?: string;
}

export async function createUser(payload: UserCreateInput): Promise<User> {
  const { data } = await apiClient.post<User>("/users", payload);
  return data;
}

export async function updateUser(id: string, payload: Partial<UserCreateInput> & { status?: string }): Promise<User> {
  const { data } = await apiClient.patch<User>(`/users/${id}`, payload);
  return data;
}

export async function fetchRolePermissions(role: Role): Promise<RolePermissionRow[]> {
  const { data } = await apiClient.get<RolePermissionRow[]>(`/roles/${role}/permissions`);
  return data;
}

export async function updateRolePermissions(role: Role, permissions: Partial<Record<PermissionKey, boolean>>): Promise<RolePermissionRow[]> {
  const { data } = await apiClient.put<RolePermissionRow[]>(`/roles/${role}/permissions`, { permissions });
  return data;
}

export async function resetRolePermissionsToDefault(role: Role): Promise<RolePermissionRow[]> {
  const { data } = await apiClient.post<RolePermissionRow[]>(`/roles/${role}/permissions/reset`);
  return data;
}
